import { Box } from '@mui/material';
import ErrorBoundary from '@/components/ErrorBoundary';
import { combineSx } from '@/utils/combineSx';
import { useValuationsStore } from '../../../store/valuationsStore';
import { Col } from '../../Col';
import { SUMMARY_ITEMS } from './constants';
import { FinancialConsiderationsView } from './FinancialConsiderationsView';
import { KeyInformationView } from './KeyInformationView';
import { LeadAnalystView } from './LeadAnalystView';
import { ProductionAndReservesView } from './ProductionAndReservesView';
import { TimelineView } from './TimelineView';

const summaryStyles = {
  container: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'repeat(12, 1fr)',
    gap: 'var(--gap-section)',
    perspective: '1000px',
    transformStyle: 'preserve-3d',
    backfaceVisibility: 'hidden',
    padding: '0.25rem',

    '&:hover': {
      '& > :not(:hover)': {
        opacity: 0.9,
      },
    },
  },
};

type AssetSummaryProps = {
  children: React.ReactChild;
};

const AssetSummary = ({ children }: AssetSummaryProps) => {
  const selectedAssetDetails = useValuationsStore.use.selectedAssetDetails();

  if (!selectedAssetDetails) {
    return <Box sx={summaryStyles.container} data-id='empty-asset-summary' />;
  }

  return (
    <ErrorBoundary>
      <Box sx={combineSx(summaryStyles.container, {})} data-id='asset-summary'>
        <Col
          columnTitle={SUMMARY_ITEMS.TIMELINE}
          data-id='asset-summary-timeline'
          gridColumn='1/-1'
        >
          <TimelineView />
        </Col>

        <Col
          key={`key-information`}
          columnTitle={SUMMARY_ITEMS.KEY_INFORMATION}
          gridColumn={{
            xs: '1/-1',
            md: 'span 9',
          }}
        >
          <KeyInformationView
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(4, 1fr)',
              xxl: 'repeat(5, 1fr)',
            }}
          />
        </Col>
        <Col
          columnTitle={SUMMARY_ITEMS.LEAD_ANALYST}
          data-id='asset-summary-timeline'
          gridColumn={{
            xs: '1/-1',
            md: 'span 3',
          }}
          variant='highlighted'
          sx={{ height: 'auto' }}
        >
          <LeadAnalystView />
        </Col>

        <Col
          key='production-reserves'
          data-id='asset-summary-production-and-reserves'
          columnTitle={SUMMARY_ITEMS.PRODUCTION_AND_RESERVES}
          gridColumn={{
            xs: '1/-1',
            md: 'span 8',
          }}
        >
          <ProductionAndReservesView
            gridTemplateColumns={{
              xs: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
              xl: 'repeat(4, 1fr)',
            }}
          />
        </Col>

        <Col
          key='asset-summary-financial-considerations'
          data-id='asset-summary-financial-considerations'
          columnTitle={SUMMARY_ITEMS.FINANCIAL_CONSIDERATIONS}
          gridColumn={{
            xs: '1/-1',
            md: 'span 4',
          }}
        >
          <FinancialConsiderationsView
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
              xxl: 'repeat(3, 1fr)',
            }}
          />
        </Col>

        <Col
          columnTitle='Asset Report'
          gridColumn={{
            xs: 'span 12',
          }}
        >
          {children}
        </Col>
      </Box>
    </ErrorBoundary>
  );
};

export { AssetSummary };
